import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  View,
} from 'react-native'
import {AppBskyFeedPost, RichText as RichTextAPI} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type RouteProp, useRoute} from '@react-navigation/native'
import {useQuery} from '@tanstack/react-query'

import {DISCOVER_FEED_URI} from '#/lib/constants'
import {getPostVideo} from '#/lib/feed/mediaHelpers'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {parseWalletsFromBio} from '#/lib/profileWallets'
import {makeProfileLink} from '#/lib/routes/links'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {shareUrl} from '#/lib/sharing'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {sanitizeHandle} from '#/lib/strings/handles'
import {getSafeUiLabel} from '#/lib/strings/ui-labels'
import {toShareUrl} from '#/lib/strings/url-helpers'
import {tellusTvDebug} from '#/lib/tv/debug'
import {getMasterRecord, resolveLongVideoThread} from '#/lib/tv/tvApi'
import {
  extractMasterUriFromVideoPage,
  extractTitleDescFromVideoPage,
  isTVSegmentPost,
  isTVVideoPage,
} from '#/lib/tv/tvParser'
import {
  getResumeEntry,
  loadWatchHistory,
  recordWatchProgress,
} from '#/lib/tv/watchHistory'
import {usePostShadow} from '#/state/cache/post-shadow'
import {POST_TOMBSTONE} from '#/state/cache/post-shadow'
import {useProfileShadow} from '#/state/cache/profile-shadow'
import {useWalletSettings} from '#/state/preferences'
import {useBookmarkMutation} from '#/state/queries/bookmarks/useBookmarkMutation'
import {
  usePostLikeMutationQueue,
  usePostQuery,
  usePostRepostMutationQueue,
} from '#/state/queries/post'
import {usePostFeedQuery} from '#/state/queries/post-feed'
import {useProfileFollowMutationQueue} from '#/state/queries/profile'
import {useProfileQuery} from '#/state/queries/profile'
import {usePostThread} from '#/state/queries/usePostThread'
import {useAgent, useRequireAuth} from '#/state/session'
import {Post} from '#/view/com/post/Post'
import {List} from '#/view/com/util/List'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {ThreadComposePrompt} from '#/screens/PostThread/components/ThreadComposePrompt'
import {atoms as a, useTheme} from '#/alf'
import {BadgeIcon} from '#/components/badges/BadgeIcon'
import {Button, ButtonText} from '#/components/Button'
import {ArrowShareRight_Stroke2_Corner2_Rounded as ShareIcon} from '#/components/icons/ArrowShareRight'
import {Bookmark, BookmarkFilled} from '#/components/icons/Bookmark'
import {Heart2_Stroke2_Corner0_Rounded as HeartIcon} from '#/components/icons/Heart2'
import {Repost_Stroke2_Corner2_Rounded as RepostIcon} from '#/components/icons/Repost'
import {Sparkle_Stroke2_Corner0_Rounded as SparkleIcon} from '#/components/icons/Sparkle'
import * as Layout from '#/components/Layout'
import {Link} from '#/components/Link'
import {PostControlButtonIcon} from '#/components/PostControls/PostControlButton'
import TipModal from '#/components/TipModal'
import {TellusTVCard} from '#/components/tv/TellusTVCard'
import {
  type PlayableSegment,
  TellusTVSegmentPlayer,
} from '#/components/tv/TellusTVSegmentPlayer'
import {Text} from '#/components/Typography'
import * as bsky from '#/types/bsky'

type WatchRoute = RouteProp<CommonNavigatorParams, 'TellusTVWatch'>
type PostShadow = Exclude<
  ReturnType<typeof usePostShadow>,
  typeof POST_TOMBSTONE
>

export function TellusTVWatchScreen() {
  const {_} = useLingui()
  const agent = useAgent()
  const {params} = useRoute<WatchRoute>()
  const videoPageUri = useMemo(() => {
    try {
      const decoded = decodeURIComponent(params.uriEncoded)
      tellusTvDebug('watch route', {
        uriEncoded: params.uriEncoded,
        videoPageUri: decoded,
      })
      return decoded
    } catch (error) {
      tellusTvDebug('watch route decode failed', {
        uriEncoded: params.uriEncoded,
        error: error instanceof Error ? error.message : String(error),
      })
      return params.uriEncoded
    }
  }, [params.uriEncoded])

  const {
    data: videoPagePost,
    error: videoPageError,
    refetch: refetchVideoPage,
  } = usePostQuery(videoPageUri)
  const masterUri = useMemo(
    () => extractMasterUriFromVideoPage(videoPagePost),
    [videoPagePost],
  )

  const {
    data: master,
    error: masterError,
    refetch: refetchMaster,
  } = useQuery({
    queryKey: ['tellustv-master', masterUri],
    queryFn: () => getMasterRecord(agent, masterUri || ''),
    enabled: !!masterUri,
  })

  const {
    data: threadData,
    error: threadError,
    refetch: refetchThread,
  } = useQuery({
    queryKey: ['tellustv-thread', videoPageUri],
    queryFn: () => resolveLongVideoThread(agent, videoPageUri),
    enabled: !!videoPagePost && !masterUri,
  })

  const resolved = useMemo(() => {
    if (master) {
      return {
        segments: master.segments,
        totalDurationMs: master.totalDurationMs,
      }
    }
    if (threadData) {
      return {
        segments: threadData.segments,
        totalDurationMs: threadData.totalDurationMs,
      }
    }
    return undefined
  }, [master, threadData])

  const loadError = useMemo(() => {
    if (videoPageError) return videoPageError
    if (masterUri) return masterError
    return threadError
  }, [videoPageError, masterUri, masterError, threadError])
  const isResolving = !loadError && (!videoPagePost || !resolved)
  const [showSlowResolve, setShowSlowResolve] = useState(false)

  useEffect(() => {
    if (!isResolving) {
      setShowSlowResolve(false)
      return
    }
    const timer = setTimeout(() => {
      setShowSlowResolve(true)
    }, 3000)
    return () => clearTimeout(timer)
  }, [isResolving])

  useEffect(() => {
    if (!videoPagePost) return
    tellusTvDebug('selected video set', {
      videoPageUri: videoPagePost.uri,
      hasMaster: Boolean(masterUri),
    })
  }, [videoPagePost, masterUri])

  useEffect(() => {
    if (!loadError) return
    tellusTvDebug('watch resolve error', {
      videoPageUri,
      hasMaster: Boolean(masterUri),
      error: loadError instanceof Error ? loadError.message : String(loadError),
    })
  }, [loadError, masterUri, videoPageUri])

  const onRetryResolve = useCallback(() => {
    tellusTvDebug('watch retry resolve', {
      videoPageUri,
      hasMaster: Boolean(masterUri),
    })
    refetchVideoPage()
    if (masterUri) {
      refetchMaster()
    } else {
      refetchThread()
    }
  }, [masterUri, refetchMaster, refetchThread, refetchVideoPage, videoPageUri])

  if (loadError) {
    return (
      <Layout.Screen testID="tellusTVWatchScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>Tellus TV</Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <View style={[a.px_lg, a.py_md, a.gap_md]}>
          <Text>{_(msg`Video unavailable.`)}</Text>
          <View>
            <Button
              label={_(msg`Retry`)}
              size="small"
              variant="solid"
              color="secondary"
              onPress={onRetryResolve}>
              <ButtonText>{_(msg`Retry`)}</ButtonText>
            </Button>
          </View>
        </View>
      </Layout.Screen>
    )
  }

  if (!videoPagePost || !resolved) {
    return (
      <Layout.Screen testID="tellusTVWatchScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>Tellus TV</Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <View style={[a.px_lg, a.py_md, a.gap_md]}>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            <ActivityIndicator />
            <Text>{_(msg`Loading video…`)}</Text>
          </View>
          {isResolving && showSlowResolve ? (
            <View>
              <Button
                label={_(msg`Retry`)}
                size="small"
                variant="solid"
                color="secondary"
                onPress={onRetryResolve}>
                <ButtonText>{_(msg`Retry`)}</ButtonText>
              </Button>
            </View>
          ) : null}
        </View>
      </Layout.Screen>
    )
  }

  if (!resolved.segments.length) {
    return (
      <Layout.Screen testID="tellusTVWatchScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>Tellus TV</Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <View style={[a.px_lg, a.py_md, a.gap_md]}>
          <Text>{_(msg`Video data is missing.`)}</Text>
          <View>
            <Button
              label={_(msg`Retry`)}
              size="small"
              variant="solid"
              color="secondary"
              onPress={onRetryResolve}>
              <ButtonText>{_(msg`Retry`)}</ButtonText>
            </Button>
          </View>
        </View>
      </Layout.Screen>
    )
  }

  return (
    <TellusTVWatchLoaded
      videoPageUri={videoPageUri}
      videoPagePost={videoPagePost}
      segments={resolved.segments}
      totalDurationMs={resolved.totalDurationMs}
    />
  )
}

function TellusTVWatchLoaded({
  videoPageUri,
  videoPagePost,
  segments,
  totalDurationMs,
}: {
  videoPageUri: string
  videoPagePost: NonNullable<ReturnType<typeof usePostQuery>['data']>
  segments: Awaited<ReturnType<typeof resolveLongVideoThread>>['segments']
  totalDurationMs?: number
}) {
  const {_} = useLingui()
  const postShadow = usePostShadow(videoPagePost)

  if (postShadow === POST_TOMBSTONE) {
    return (
      <Layout.Screen testID="tellusTVWatchScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>Tellus TV</Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
        <View style={[a.px_lg, a.py_md]}>
          <Text>{_(msg`This video is no longer available.`)}</Text>
        </View>
      </Layout.Screen>
    )
  }

  return (
    <TellusTVWatchContent
      videoPageUri={videoPageUri}
      videoPagePost={videoPagePost}
      segments={segments}
      totalDurationMs={totalDurationMs}
      postShadow={postShadow}
    />
  )
}

function TellusTVWatchContent({
  videoPageUri,
  videoPagePost,
  segments,
  totalDurationMs,
  postShadow,
}: {
  videoPageUri: string
  videoPagePost: NonNullable<ReturnType<typeof usePostQuery>['data']>
  segments: Awaited<ReturnType<typeof resolveLongVideoThread>>['segments']
  totalDurationMs?: number
  postShadow: PostShadow
}) {
  const t = useTheme()
  const {_} = useLingui()
  const agent = useAgent()
  const requireAuth = useRequireAuth()
  const {openComposer} = useOpenComposer()
  const {mutateAsync: bookmark} = useBookmarkMutation()
  const {width} = useWindowDimensions()

  const {data: authorProfile} = useProfileQuery({
    did: videoPagePost.author.did,
    enabled: !!videoPagePost.author.did,
  })
  const profileShadow = useProfileShadow(authorProfile || videoPagePost.author)
  const [queueFollow, queueUnfollow] = useProfileFollowMutationQueue(
    profileShadow,
    'ProfileCard',
  )

  const record = useMemo(() => {
    return bsky.validate(videoPagePost.record, AppBskyFeedPost.validateRecord)
      ? (videoPagePost.record as AppBskyFeedPost.Record)
      : undefined
  }, [videoPagePost.record])

  const richText = useMemo(() => {
    if (!record) return undefined
    return new RichTextAPI({text: record.text, facets: record.facets})
  }, [record])

  const {title, description} = useMemo(
    () => extractTitleDescFromVideoPage(videoPagePost),
    [videoPagePost],
  )
  const safeTitle = getSafeUiLabel(title, {
    context: 'tellustv-watch-title',
    fallback: '',
  })
  const authorName = sanitizeDisplayName(
    profileShadow.displayName || profileShadow.handle,
  )
  const authorHandle = sanitizeHandle(profileShadow.handle, '@')

  const {data: resumeEntry} = useQuery({
    queryKey: ['tellustv-resume', videoPageUri],
    queryFn: () => getResumeEntry(videoPageUri),
    enabled: !!videoPageUri,
  })

  const [queueLike, queueUnlike] = usePostLikeMutationQueue(
    postShadow,
    undefined,
    undefined,
    'Post',
  )
  const [queueRepost, queueUnrepost] = usePostRepostMutationQueue(
    postShadow,
    undefined,
    undefined,
    'Post',
  )

  const thread = usePostThread({anchor: videoPageUri})
  const comments = useMemo(() => {
    if (!thread.data.items.length) return []
    return thread.data.items
      .filter(item => item.type === 'threadPost' && item.depth > 0)
      .map(item => item.value.post)
      .filter(post => !isTVSegmentPost(post))
  }, [thread.data.items])

  const discoverFeedDesc = `feedgen|${DISCOVER_FEED_URI}`
  const {data: discoverData} = usePostFeedQuery(discoverFeedDesc)
  const {data: followingData} = usePostFeedQuery('following')
  const {data: historyState} = useQuery({
    queryKey: ['tellustv-history'],
    queryFn: () => loadWatchHistory(),
  })
  const upNext = useMemo(() => {
    const posts: (typeof videoPagePost)[] = []
    const dedupe = new Map<string, typeof videoPagePost>()
    const sourcePages =
      discoverData?.pages && discoverData.pages.length
        ? discoverData.pages
        : followingData?.pages
    if (sourcePages) {
      for (const page of sourcePages) {
        for (const slice of page.slices) {
          for (const item of slice.items) {
            if (item.post.uri === videoPageUri) continue
            if (isTVSegmentPost(item.post)) continue
            const isTVRoot = isTVVideoPage(item.post)
            const hasVideo = Boolean(getPostVideo(item.post)?.url)
            if (!isTVRoot && !hasVideo) continue
            if (!dedupe.has(item.post.uri)) {
              dedupe.set(item.post.uri, item.post)
            }
          }
        }
      }
    }
    posts.push(...dedupe.values())
    const now = Date.now()
    const scored = posts.map(post => {
      const indexedAt = new Date(post.indexedAt).getTime()
      const recencyHours = (now - indexedAt) / 36e5
      const recencyScore = Math.max(0, 48 - recencyHours)
      const followBoost = post.author.viewer?.following ? 10 : 0
      const likedBoost = post.viewer?.like ? 4 : 0
      const authorWatchMs = historyState?.authorWatchMs[post.author.did] || 0
      const watchBoost = Math.min(8, authorWatchMs / 600000)
      const watchedPenalty = historyState?.entries[post.uri] ? -6 : 0
      return {
        post,
        score:
          recencyScore + followBoost + likedBoost + watchBoost + watchedPenalty,
      }
    })
    return scored
      .sort((itemA, itemB) => itemB.score - itemA.score)
      .map(item => item.post)
      .slice(0, 12)
  }, [discoverData, followingData, historyState, videoPageUri])

  const onPressReply = useCallback(() => {
    if (!record || !richText) return
    requireAuth(() => {
      openComposer({
        replyTo: {
          uri: videoPagePost.uri,
          cid: videoPagePost.cid,
          text: record.text,
          author: videoPagePost.author,
          embed: videoPagePost.embed,
          moderation: undefined,
          langs: record.langs,
        },
      })
    })
  }, [openComposer, record, requireAuth, richText, videoPagePost])

  const onPressPromo = useCallback(() => {
    const promoText = `${safeTitle ? `📺 ${safeTitle}\n` : ''}Tellus TV`
    requireAuth(() => {
      openComposer({
        quote: videoPagePost,
        text: promoText,
      })
    })
  }, [openComposer, requireAuth, safeTitle, videoPagePost])

  const onShare = useCallback(() => {
    const sharePath = `/tv/watch/${encodeURIComponent(videoPageUri)}`
    shareUrl(toShareUrl(sharePath))
  }, [videoPageUri])

  const onToggleBookmark = useCallback(() => {
    requireAuth(async () => {
      if (postShadow.viewer?.bookmarked) {
        await bookmark({action: 'delete', uri: postShadow.uri})
      } else {
        await bookmark({action: 'create', post: postShadow})
      }
    })
  }, [bookmark, postShadow, requireAuth])

  const lastTimeRef = useRef(0)
  const onProgress = useCallback(
    (info: {timeMs: number; segmentIndex: number; offsetMs: number}) => {
      const now = Date.now()
      const deltaMs = info.timeMs - lastTimeRef.current
      lastTimeRef.current = info.timeMs
      recordWatchProgress({
        entry: {
          videoPageUri,
          segmentIndex: info.segmentIndex,
          offsetMs: info.offsetMs,
          positionMs: info.timeMs,
          updatedAt: now,
          totalDurationMs: totalDurationMs || 0,
          title: safeTitle || undefined,
          authorDid: videoPagePost.author.did,
        },
        deltaMs: deltaMs > 0 ? deltaMs : undefined,
      })
    },
    [safeTitle, totalDurationMs, videoPagePost.author.did, videoPageUri],
  )

  const [tipOpen, setTipOpen] = useState(false)
  const {showInSidebar: walletUiEnabled} = useWalletSettings()
  const parsedWallets = useMemo(
    () => parseWalletsFromBio(authorProfile?.description || ''),
    [authorProfile?.description],
  )
  const hasTipTarget =
    walletUiEnabled &&
    Boolean(
      parsedWallets.evm?.address ||
        parsedWallets.lightning?.address ||
        parsedWallets.solana?.address ||
        parsedWallets.bitcoin?.address,
    )
  const canTip = Platform.OS === 'web' && hasTipTarget
  const orderedSegments = useMemo(() => {
    const next = [...segments]
    if (next.some(segment => segment.order != null)) {
      next.sort((segmentA, segmentB) => {
        return (segmentA.order ?? 0) - (segmentB.order ?? 0)
      })
    }
    return next
  }, [segments])

  const {
    data: playableSegments,
    error: playableSegmentsError,
    isLoading: isPlayableSegmentsLoading,
    isFetching: isPlayableSegmentsFetching,
    refetch: refetchPlayableSegments,
  } = useQuery({
    queryKey: [
      'tellustv-playable',
      orderedSegments.map(seg => seg.uri).join('|'),
    ],
    queryFn: async () => {
      tellusTvDebug('playable fetch start', {
        videoPageUri,
        segmentCount: orderedSegments.length,
      })
      const getPdsBaseUrl = () => {
        const anyAgent = agent as any
        const url =
          anyAgent?.pdsUrl?.toString?.() ||
          anyAgent?.sessionManager?.pdsUrl?.toString?.() ||
          anyAgent?.dispatchUrl?.toString?.()
        if (!url) return undefined
        try {
          return new URL(url).origin
        } catch {
          return undefined
        }
      }
      const uris = orderedSegments.map(seg => seg.uri)
      const posts: any[] = []
      const chunkSize = 20
      for (let i = 0; i < uris.length; i += chunkSize) {
        const chunk = uris.slice(i, i + chunkSize)
        const res = await agent.app.bsky.feed.getPosts({uris: chunk})
        posts.push(...res.data.posts)
        tellusTvDebug('playable fetch chunk end', {
          chunkStart: i,
          chunkSize: chunk.length,
          returned: res.data.posts.length,
        })
      }
      const postMap = new Map(posts.map(post => [post.uri, post]))
      const playable: PlayableSegment[] = []
      orderedSegments.forEach((segment, index) => {
        const post = postMap.get(segment.uri)
        const video = post ? getPostVideo(post) : undefined
        let src = video?.url
        let thumb = video?.thumb
        if (!src && post?.record?.embed?.$type === 'app.bsky.embed.video') {
          const blob = post.record.embed.video
          const cid =
            blob?.ref?.$link ||
            blob?.ref ||
            blob?.cid ||
            blob?.$link ||
            undefined
          const did = post.author?.did
          const pdsBase = getPdsBaseUrl()
          if (cid && did && pdsBase) {
            src = `${pdsBase}/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(
              did,
            )}&cid=${encodeURIComponent(cid)}`
          }
        }
        if (!src) {
          return
        }
        if (src.includes('mainnet.base.org')) {
          return
        }
        playable.push({
          uri: segment.uri,
          order: segment.order ?? index,
          durationMs: segment.durationMs || 0,
          src,
          thumb,
        })
      })
      tellusTvDebug('playable fetch end', {
        videoPageUri,
        playableCount: playable.length,
      })
      return playable
    },
    enabled: !!orderedSegments.length,
  })
  const isPlayableLoading =
    !playableSegments &&
    (isPlayableSegmentsLoading || isPlayableSegmentsFetching)
  const [showSlowPlayableLoad, setShowSlowPlayableLoad] = useState(false)
  const [playerError, setPlayerError] = useState<Error | null>(null)
  const [playerReloadKey, setPlayerReloadKey] = useState(0)

  useEffect(() => {
    if (!isPlayableLoading) {
      setShowSlowPlayableLoad(false)
      return
    }
    const timer = setTimeout(() => {
      setShowSlowPlayableLoad(true)
    }, 3000)
    return () => clearTimeout(timer)
  }, [isPlayableLoading])

  useEffect(() => {
    if (playableSegmentsError) {
      tellusTvDebug('playable fetch error', {
        videoPageUri,
        error:
          playableSegmentsError instanceof Error
            ? playableSegmentsError.message
            : String(playableSegmentsError),
      })
    }
  }, [playableSegmentsError, videoPageUri])

  useEffect(() => {
    if (playableSegments?.length) {
      setPlayerError(null)
    }
  }, [playableSegments?.length])

  const onPlayerError = useCallback(
    (error: Error) => {
      setPlayerError(error)
      tellusTvDebug('player reported error', {
        videoPageUri,
        error: error.message,
      })
    },
    [videoPageUri],
  )

  const onRetryPlayback = useCallback(() => {
    setPlayerError(null)
    setPlayerReloadKey(prev => prev + 1)
    tellusTvDebug('player retry', {videoPageUri})
    refetchPlayableSegments()
  }, [refetchPlayableSegments, videoPageUri])

  const resolvedTotalDurationMs = useMemo(() => {
    if (totalDurationMs && totalDurationMs > 0) return totalDurationMs
    if (playableSegments?.length) {
      return playableSegments.reduce(
        (acc, segment) => acc + (segment.durationMs || 0),
        0,
      )
    }
    return orderedSegments.reduce(
      (acc, segment) => acc + (segment.durationMs || 0),
      0,
    )
  }, [orderedSegments, playableSegments, totalDurationMs])

  const header = useMemo(() => {
    const isDesktop = width >= 1280
    const contentPadding = 12
    const pageMaxWidth = 1920
    const columnGap = 24
    const rightRailWidth = 400
    const minLeftWidth = 760
    const maxLeftWidth = 1120
    const contentWidth = Math.max(
      0,
      Math.min(width - contentPadding * 2, pageMaxWidth),
    )
    return (
      <View
        style={[
          {
            paddingHorizontal: contentPadding,
            paddingTop: 16,
            gap: 12,
            maxWidth: pageMaxWidth,
            width: '100%',
            alignSelf: 'flex-start',
          },
        ]}>
        <View style={[a.flex_row, a.align_center, a.gap_sm]}>
          <Layout.Header.BackButton />
          <Text style={[a.text_lg, a.font_bold]}>{_(msg`Tellus TV`)}</Text>
        </View>
        <View
          style={[
            {
              flexDirection: isDesktop ? 'row' : 'column',
              gap: columnGap,
              alignItems: 'flex-start',
            },
          ]}>
          <View
            style={[
              {
                flex: isDesktop ? 1 : undefined,
                minWidth: isDesktop ? minLeftWidth : 0,
                maxWidth: isDesktop ? maxLeftWidth : undefined,
                width: isDesktop ? undefined : '100%',
                gap: 12,
              },
            ]}>
            {playableSegments?.length ? (
              <View style={[a.gap_sm]}>
                <TellusTVSegmentPlayer
                  key={`${videoPageUri}:${playerReloadKey}`}
                  segments={playableSegments}
                  totalDurationMs={resolvedTotalDurationMs}
                  initialTimeMs={resumeEntry?.positionMs}
                  onProgress={onProgress}
                  onError={onPlayerError}
                />
                {playerError || playableSegmentsError ? (
                  <View
                    style={[
                      a.rounded_md,
                      a.p_md,
                      t.atoms.bg_contrast_25,
                      a.gap_sm,
                    ]}>
                    <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                      {_(msg`Video unavailable.`)}
                    </Text>
                    <View>
                      <Button
                        label={_(msg`Retry`)}
                        size="small"
                        variant="solid"
                        color="secondary"
                        onPress={onRetryPlayback}>
                        <ButtonText>{_(msg`Retry`)}</ButtonText>
                      </Button>
                    </View>
                  </View>
                ) : null}
              </View>
            ) : (
              <View
                style={[
                  a.rounded_md,
                  a.overflow_hidden,
                  a.align_center,
                  a.justify_center,
                  a.gap_sm,
                  a.p_md,
                  {backgroundColor: '#000', aspectRatio: 16 / 9},
                ]}>
                {isPlayableLoading ? (
                  <>
                    <ActivityIndicator color="white" />
                    <Text style={[a.text_sm, {color: 'white'}]}>
                      {_(msg`Loading video…`)}
                    </Text>
                    {showSlowPlayableLoad ? (
                      <Button
                        label={_(msg`Retry`)}
                        size="small"
                        variant="solid"
                        color="secondary"
                        onPress={onRetryPlayback}>
                        <ButtonText>{_(msg`Retry`)}</ButtonText>
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Text style={[a.text_sm, {color: 'white'}]}>
                      {_(msg`Video is still processing or unavailable.`)}
                    </Text>
                    <Button
                      label={_(msg`Retry`)}
                      size="small"
                      variant="solid"
                      color="secondary"
                      onPress={onRetryPlayback}>
                      <ButtonText>{_(msg`Retry`)}</ButtonText>
                    </Button>
                  </>
                )}
              </View>
            )}
            <View style={[a.gap_sm]}>
              <Text style={[a.text_xl, a.font_bold]}>
                {safeTitle || _(msg`Tellus TV`)}
              </Text>
              <View
                style={[
                  {
                    flexDirection: isDesktop ? 'row' : 'column',
                    alignItems: isDesktop ? 'center' : 'flex-start',
                    gap: 12,
                  },
                ]}>
                <View
                  style={[
                    a.flex_row,
                    a.align_center,
                    a.gap_sm,
                    {flex: 1, minWidth: 0},
                  ]}>
                  <Link to={makeProfileLink(profileShadow)}>
                    <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                      <UserAvatar
                        type="user"
                        size={40}
                        avatar={profileShadow.avatar}
                      />
                      <View style={[a.flex_col, a.gap_2xs, a.flex_shrink]}>
                        <View style={[a.flex_row, a.align_center, a.gap_2xs]}>
                          <Text style={[a.text_sm, a.font_bold]}>
                            {authorName}
                          </Text>
                          <BadgeIcon actor={profileShadow} size={12} />
                        </View>
                        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                          {authorHandle}
                        </Text>
                      </View>
                    </View>
                  </Link>
                  {profileShadow.viewer?.following != null ? (
                    <Button
                      size="small"
                      variant="solid"
                      color="secondary"
                      label={
                        profileShadow.viewer.following
                          ? _(msg`Following`)
                          : _(msg`Follow`)
                      }
                      onPress={() =>
                        profileShadow.viewer.following
                          ? queueUnfollow()
                          : queueFollow()
                      }>
                      <ButtonText>
                        {profileShadow.viewer.following
                          ? _(msg`Following`)
                          : _(msg`Follow`)}
                      </ButtonText>
                    </Button>
                  ) : null}
                </View>
                <View
                  style={[
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    },
                  ]}>
                  <ActionPillButton
                    label={_(msg`Like`)}
                    icon={HeartIcon}
                    active={!!postShadow.viewer?.like}
                    activeColor={t.palette.positive_500}
                    onPress={() =>
                      requireAuth(() =>
                        postShadow.viewer?.like ? queueUnlike() : queueLike(),
                      )
                    }
                  />
                  <ActionPillButton
                    label={_(msg`Repost`)}
                    icon={RepostIcon}
                    active={!!postShadow.viewer?.repost}
                    activeColor={t.palette.primary_500}
                    onPress={() =>
                      requireAuth(() =>
                        postShadow.viewer?.repost
                          ? queueUnrepost()
                          : queueRepost(),
                      )
                    }
                  />
                  <ActionPillButton
                    label={_(msg`Watch later`)}
                    icon={
                      postShadow.viewer?.bookmarked ? BookmarkFilled : Bookmark
                    }
                    active={!!postShadow.viewer?.bookmarked}
                    activeColor={t.palette.primary_500}
                    onPress={onToggleBookmark}
                  />
                  <ActionPillButton
                    label={_(msg`Share`)}
                    icon={ShareIcon}
                    onPress={onShare}
                  />
                  <ActionPillButton
                    label={_(msg`Post promo`)}
                    icon={SparkleIcon}
                    onPress={onPressPromo}
                  />
                  {canTip ? (
                    <ActionPillButton
                      label={_(msg`Tip`)}
                      icon={SparkleIcon}
                      onPress={() => setTipOpen(true)}
                    />
                  ) : null}
                </View>
              </View>
            </View>
            {description ? (
              <View
                style={[
                  a.rounded_md,
                  a.p_md,
                  t.atoms.bg_contrast_25,
                  t.atoms.border_contrast_low,
                  {borderWidth: 1},
                  Platform.OS === 'web'
                    ? ({
                        backdropFilter: 'blur(12px)',
                      } as any)
                    : null,
                ]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  {description}
                </Text>
              </View>
            ) : null}
            <View style={[a.pt_sm]}>
              <ThreadComposePrompt onPressCompose={onPressReply} />
            </View>
            <View style={[a.pt_md]}>
              <Text style={[a.text_lg, a.font_bold]}>{_(msg`Comments`)}</Text>
            </View>
          </View>
          {isDesktop ? (
            <View style={[{width: rightRailWidth}, a.gap_md]}>
              <Text style={[a.text_md, a.font_bold]}>{_(msg`Up next`)}</Text>
              {upNext.length ? (
                <View style={[{gap: 12}]}>
                  {upNext.map(post => (
                    <TellusTVCard
                      key={post.uri}
                      post={post}
                      variant="compact"
                      width={rightRailWidth}
                    />
                  ))}
                </View>
              ) : (
                <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    {_(msg`More videos soon.`)}
                  </Text>
                </View>
              )}
            </View>
          ) : null}
        </View>
        {!isDesktop ? (
          <View style={[a.gap_md]}>
            <Text style={[a.text_md, a.font_bold]}>{_(msg`Up next`)}</Text>
            {upNext.length ? (
              <View style={[{gap: 12}]}>
                {upNext.map(post => (
                  <TellusTVCard
                    key={post.uri}
                    post={post}
                    variant="compact"
                    width={contentWidth}
                  />
                ))}
              </View>
            ) : (
              <View style={[a.rounded_md, a.p_md, t.atoms.bg_contrast_25]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  {_(msg`More videos soon.`)}
                </Text>
              </View>
            )}
          </View>
        ) : null}
      </View>
    )
  }, [
    description,
    canTip,
    resolvedTotalDurationMs,
    isPlayableLoading,
    playableSegments,
    playableSegmentsError,
    playerError,
    playerReloadKey,
    onPlayerError,
    onPressPromo,
    onPressReply,
    onProgress,
    onRetryPlayback,
    onShare,
    onToggleBookmark,
    postShadow.viewer?.bookmarked,
    postShadow.viewer?.like,
    postShadow.viewer?.repost,
    queueFollow,
    queueLike,
    queueRepost,
    queueUnfollow,
    queueUnlike,
    queueUnrepost,
    requireAuth,
    resumeEntry?.positionMs,
    safeTitle,
    showSlowPlayableLoad,
    authorHandle,
    authorName,
    profileShadow,
    t,
    upNext,
    videoPageUri,
    width,
    _,
  ])

  return (
    <Layout.Screen testID="tellusTVWatchScreen">
      <List
        data={comments}
        keyExtractor={item => item.uri}
        renderItem={({item}) => <Post post={item} />}
        ListHeaderComponent={header}
        ListFooterComponent={<View style={[a.pb_xl]} />}
        contentContainerStyle={[a.pb_xl, {width: '100%', alignSelf: 'stretch'}]}
        style={[{width: '100%'}]}
        disableCentering
      />
      {canTip ? (
        <TipModal
          open={tipOpen}
          onClose={() => setTipOpen(false)}
          target={{
            evm: parsedWallets.evm
              ? {
                  address: parsedWallets.evm.address,
                  network: parsedWallets.evm.network,
                }
              : undefined,
            solana: parsedWallets.solana?.address,
            bitcoin: parsedWallets.bitcoin
              ? {
                  address: parsedWallets.bitcoin.address,
                  network: parsedWallets.bitcoin.network,
                }
              : undefined,
            lightning: parsedWallets.lightning?.address,
          }}
        />
      ) : null}
    </Layout.Screen>
  )
}

function ActionPillButton({
  label,
  icon,
  onPress,
  active,
  activeColor,
}: {
  label: string
  icon: ComponentType<{size?: string | number; style?: any; fill?: string}>
  onPress: () => void
  active?: boolean
  activeColor?: string
}) {
  const t = useTheme()
  return (
    <View>
      <Button
        onPress={onPress}
        label={label}
        variant="ghost"
        color="secondary"
        size="small"
        shape="round"
        style={[
          a.flex_row,
          a.align_center,
          a.justify_center,
          {
            height: 36,
            width: 36,
            backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
          },
        ]}
        hoverStyle={[
          {
            backgroundColor: t.atoms.bg_contrast_50.backgroundColor,
          },
        ]}>
        {({hovered, pressed}) => (
          <View
            style={[
              a.align_center,
              a.justify_center,
              a.transition_transform,
              {
                transform: [{scale: pressed ? 0.92 : hovered ? 1.05 : 1}],
              },
            ]}>
            <PostControlButtonIcon
              icon={icon}
              style={[{color: active && activeColor ? activeColor : undefined}]}
              fill={
                active && activeColor ? activeColor : t.palette.contrast_700
              }
            />
          </View>
        )}
      </Button>
    </View>
  )
}
